import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';
import { cssMap } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box } from '@atlaskit/primitives/compiled';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { SpotlightCard, SpotlightPulse } from '@atlaskit/onboarding';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { type Placement, Manager, Popper, Reference } from '@atlaskit/popper';
import Portal from '@atlaskit/portal';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets';
import {
	useSetOnboardingState,
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { AIFC_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';

import SpotlightImage from '../assets/SpotlightGif.gif';

import { useIsAIFCOnbardingEligible } from './hooks/useIsAIFCOnbardingEligible';

const styles = cssMap({
	image: {
		minWidth: '275px',
		minHeight: '159px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage,
		backgroundColor: '#CFE1FD',
	},
	pulseWrapper: {
		marginInline: token('space.075'),
	},
});

const i18n = defineMessages({
	smartCreateTitle: {
		id: 'onboarding-ai-first-create.nudge.smart-create-with-ai.title',
		defaultMessage: 'Rovo can create it',
		description: 'Title for smart create with AI spotlight',
	},
	smartCreateDescription: {
		id: 'onboarding-ai-first-create.nudge.smart-create-with-ai.description',
		defaultMessage:
			'Let Rovo draft that doc or whiteboard you need - fast - from a simple prompt and relevant work.',
		description: 'Description text for smart create with AI spotlight',
	},
	smartCreateCTA: {
		id: 'onboarding-ai-first-create.nudge.smart-create-with-ai.cta',
		defaultMessage: 'Try it now',
		description: 'CTA button text for smart create with AI spotlight',
	},
});

export type AISmartCreateSpotlightProps = {
	children?:
		| (() => React.ReactNode)
		| ((isSpotlightActive: boolean) => React.ReactNode)
		| React.JSX.Element;
	onClick?: () => void;
	isLoading?: boolean;
	source: string;
	placement?: Placement;
};

export const AISmartCreateSpotlight = ({
	children,
	onClick,
	source,
	isLoading,
	placement = 'left-start',
}: AISmartCreateSpotlightProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useSetOnboardingState();
	const { useChoreographer } = useChoreographerAssets();
	const [isSpotlightActive, setIsSpotlightActive] = useState(false);

	const handleSpotlightStart = () => {
		setOnboardingState([
			{
				key: AIFC_ONBOARDING_STATE_KEYS.hasSeenAIFCSpotlight,
				value: 'true',
			},
		]);

		setIsSpotlightActive(true);

		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'AISmartCreateSpotlight',
				attributes: {
					source,
				},
			},
		}).fire();
	};

	const handleSpotlightStop = () => {
		setIsSpotlightActive(false);
	};

	const choreographer = useChoreographer('AISmartCreateSpotlight', {
		start: handleSpotlightStart,
		stop: handleSpotlightStop,
	});

	const {
		isAIFCOnbardingEligible: isChangboardingEligible,
		loading: isChangboardingEligibleLoading,
		error: isChangboardingEligibleError,
	} = useIsAIFCOnbardingEligible();

	const {
		data: onboardingStateData,
		loading: isOnboardingStateLoading,
		error: isOnboardingStateError,
	} = useGetOnboardingState(
		[...Object.values(AIFC_ONBOARDING_STATE_KEYS)],
		!fg('aifc_onboarding_killswitch') || !fg('aifc_create_enabled'),
	);

	const { hasSeenAIFCSpotlight } = deserializeState(onboardingStateData);

	const handleSpotlightInteraction = useCallback(() => {
		onClick?.();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'AISmartCreateSpotlightCTA',
				source,
			},
		}).fire();

		choreographer.stopMessage();
	}, [choreographer, createAnalyticsEvent, onClick, source]);

	useEffect(() => {
		const loadingComplete =
			!isLoading &&
			!isChangboardingEligibleLoading &&
			!isChangboardingEligibleError &&
			!isOnboardingStateLoading &&
			!isOnboardingStateError;

		if (loadingComplete && isChangboardingEligible && !hasSeenAIFCSpotlight) {
			choreographer.startMessage();
		}
	}, [
		choreographer,
		hasSeenAIFCSpotlight,
		isLoading,
		isChangboardingEligible,
		isChangboardingEligibleError,
		isChangboardingEligibleLoading,
		isOnboardingStateError,
		isOnboardingStateLoading,
	]);

	const CreateWithRovoSpotlightImage = useMemo(() => {
		return (
			<div css={styles.image}>
				<img src={SpotlightImage} alt="" width={275} />
			</div>
		);
	}, []);

	return (
		<Manager>
			<Reference>
				{({ ref }) => (
					<Box
						ref={ref}
						xcss={source.includes('ContextualCreate') && isSpotlightActive && styles.pulseWrapper}
					>
						<SpotlightPulse radius={3} pulse={isSpotlightActive}>
							{typeof children === 'function' ? children(isSpotlightActive) : children}
						</SpotlightPulse>
					</Box>
				)}
			</Reference>
			{isSpotlightActive && (
				<Popper strategy="absolute" placement={placement} offset={[-2, 16]}>
					{({ ref, style }) => (
						<Portal zIndex={600}>
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop */}
							<div ref={ref} style={style}>
								<SpotlightCard
									width={275}
									heading={formatMessage(i18n.smartCreateTitle)}
									image={CreateWithRovoSpotlightImage}
									actions={[
										{
											text: formatMessage(i18n.smartCreateCTA),
											onClick: () => {
												handleSpotlightInteraction();
											},
										},
									]}
								>
									{formatMessage(i18n.smartCreateDescription)}
								</SpotlightCard>
							</div>
						</Portal>
					)}
				</Popper>
			)}
		</Manager>
	);
};
